import React, { useState, useEffect } from 'react';
import { getInitialData, getTeamData, getWeekStatus, getCurrentUser, getUserTeams } from '../DatabaseAPI';
import AnalyticsDebugger from './AnalyticsDebugger';

// Import all pages
import TeamPage from '../pages/TeamPage';
import GearPage from '../pages/GearPage';
import StandingsPage from '../pages/StandingsPage';
import DraftPage from '../pages/DraftPage';

// Import Real Login system components
import AuthenticationScreen from '../components/auth/AuthenticationScreen';
import BetaKeyRequest from '../components/auth/BetaKeyRequest';
import BetaKeyValidation from '../components/auth/BetaKeyValidation';
import UserRegistration from '../components/auth/UserRegistration';
import MyTeams from '../pages/MyTeamsPage';
import LeagueSignUp from '../pages/LeagueSignUpPage';
import PrivateLeagueCodeModal from '../components/league/PrivateLeagueCodeModal';
import LeagueCodeSuccessModal from '../components/league/LeagueCodeSuccessModal';
import TeamNameModal from '../components/league/TeamNameModal';

// Import all components
import DraftSystemDetector from '../components/draft/DraftSystemDetector';
import GearPanel from '../components/gear/GearPanel';
import Standings from '../components/league/Standings';
import TeamRankingRow from '../components/league/TeamRankingRow';
import PlayerCard from '../components/player/PlayerCard';
import GearSelectionPanel from '../components/player/GearSelectionPanel';
import PlayerEquipmentPanel from '../components/player/PlayerEquipmentPanel';
import PlayerImagePanel from '../components/player/PlayerImagePanel';
import PlayerScorePanel from '../components/player/PlayerScorePanel';
import ExpandablePlayerCard from '../components/team/ExpandablePlayerCard';
import NavigationHeader from '../components/team/NavigationHeader';
import TeamHeader from '../components/team/TeamHeader';
import TeamRoster from '../components/team/TeamRoster';

// Debug constants - will be populated from initial data
let DEBUG_CONFIG = {
    LEAGUE_ID: 1,
    TEAM_ID: null,
    LEAGUE_NAME: 'Debug League',
    USER: null
};

// Debug wrapper for StandingsPage that uses mock data instead of broken API
function DebugStandingsPage({ user, setPage, leagueId, leagueName, setSelectedOpponentTeam }) {
    const mockStandings = [
        { id: 1, name: user?.name || 'Your Team', score: 456.2, wins: 6, losses: 1 },
        { id: 2, name: 'Derek\'s Team', score: 423.1, wins: 5, losses: 2 },
        { id: 3, name: 'Mike\'s Team', score: 389.7, wins: 4, losses: 3 },
        { id: 4, name: 'Sarah\'s Team', score: 367.4, wins: 3, losses: 4 },
        { id: 5, name: 'Bob\'s Team', score: 334.8, wins: 2, losses: 5 }
    ];

    const handleTeamClick = (team) => {
        console.log('Team clicked:', team);
        setSelectedOpponentTeam && setSelectedOpponentTeam(team);
    };

    return (
        <div style={{
            backgroundColor: '#1a202c',
            minHeight: '100vh',
            color: '#f7fafc'
        }}>
            {/* Header Navigation */}
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '15px 0',
                backgroundColor: '#2d3748',
                borderBottom: '3px solid #4a5568'
            }}>
                <button
                    onClick={() => setPage && setPage()}
                    style={{
                        padding: '12px 24px',
                        marginRight: '10px',
                        backgroundColor: '#3182ce',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        textTransform: 'uppercase'
                    }}
                >
                    BACK
                </button>
                <div style={{ color: '#e2e8f0', fontSize: '12px', padding: '10px' }}>
                    🐛 DEBUG MODE - Using mock standings data (API endpoint not implemented)
                </div>
            </div>

            {/* Main Content */}
            <div style={{
                maxWidth: '800px',
                margin: '0 auto',
                padding: '20px'
            }}>
                {/* League Header */}
                <div style={{
                    textAlign: 'center',
                    marginBottom: '30px'
                }}>
                    <h1 style={{
                        fontSize: '28px',
                        fontWeight: 'bold',
                        margin: '0 0 10px 0',
                        textTransform: 'uppercase',
                        color: '#63b3ed'
                    }}>
                        LEAGUE STANDINGS
                    </h1>
                    <p style={{
                        fontSize: '16px',
                        color: '#a0aec0',
                        margin: 0
                    }}>
                        {leagueName || 'Debug League'}
                    </p>
                </div>

                {/* Standings List */}
                <div style={{
                    backgroundColor: '#2d3748',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                    {mockStandings.map((team, index) => (
                        <TeamRankingRow
                            key={team.id}
                            team={team}
                            rank={index + 1}
                            onTeamClick={handleTeamClick}
                            isCurrentUser={team.id === user?.id}
                        />
                    ))}
                </div>

                {/* Instructions */}
                <div style={{
                    textAlign: 'center',
                    marginTop: '20px',
                    fontSize: '14px',
                    color: '#a0aec0'
                }}>
                    Click on any team to view their roster and gear (Debug Mode)
                </div>
            </div>
        </div>
    );
}

function DebugSystem({ setPage }) {
    const [debugMode, setDebugMode] = useState('pages'); // 'pages' or 'components'
    const [selectedItem, setSelectedItem] = useState(null);
    const [realData, setRealData] = useState({});
    const [dataLoading, setDataLoading] = useState(true);

    // Load real data on mount
    useEffect(() => {
        loadRealData();
    }, []);

    const loadRealData = async () => {
        setDataLoading(true);
        try {
            // First try to get Real Login user teams (if authenticated)
            let firstTeam = null;
            let initialData = null;
            
            try {
                const currentUser = await getCurrentUser();
                if (currentUser) {
                    console.log('Debug: Found authenticated user:', currentUser);
                    const userTeamsData = await getUserTeams();
                    console.log('Debug: User teams data:', userTeamsData);
                    
                    if (userTeamsData && userTeamsData.teams && userTeamsData.teams.length > 0) {
                        firstTeam = userTeamsData.teams[0];
                        console.log('Debug: Using Real Login team:', firstTeam);
                    }
                }
            } catch (authError) {
                console.log('Debug: No authenticated user, trying legacy teams');
            }
            
            // Fallback to old initial data if no Real Login teams found
            if (!firstTeam) {
                initialData = await getInitialData();
                console.log('Debug: Legacy initial data:', initialData);
                
                if (initialData && initialData.teams && initialData.teams.length > 0) {
                    firstTeam = initialData.teams[0];
                } else if (initialData && initialData.leagues && initialData.leagues.length > 0) {
                    const firstLeague = initialData.leagues[0];
                    if (firstLeague.teams && firstLeague.teams.length > 0) {
                        firstTeam = firstLeague.teams[0];
                    }
                }
            }
            
            // Update DEBUG_CONFIG with found team
            if (firstTeam) {
                DEBUG_CONFIG.TEAM_ID = firstTeam.id;
                // Real Login teams use league_id (underscore), legacy teams use leagueId (camelCase)
                DEBUG_CONFIG.LEAGUE_ID = firstTeam.league_id || firstTeam.leagueId || 1;
                DEBUG_CONFIG.USER = {
                    id: firstTeam.id,
                    name: firstTeam.name || 'Debug Team',
                    packs: firstTeam.packs  // API sends dynamic count as 'packs' field
                };
                console.log('Debug: Using team:', firstTeam);
                console.log('Debug: Team ID:', DEBUG_CONFIG.TEAM_ID, 'League ID:', DEBUG_CONFIG.LEAGUE_ID);
                console.log('Debug: Team object structure:', JSON.stringify(firstTeam, null, 2));
            } else {
                // This should never happen if data is properly initialized
                throw new Error('No teams found - run data initialization first');
            }

            // Now get team and week data with the correct team ID
            const [teamData, weekStatus] = await Promise.all([
                getTeamData(DEBUG_CONFIG.TEAM_ID),
                getWeekStatus(DEBUG_CONFIG.LEAGUE_ID)
            ]);

            setRealData({
                initialData,
                teamData,
                weekStatus,
                user: DEBUG_CONFIG.USER,
                leagueId: DEBUG_CONFIG.LEAGUE_ID,
                leagueName: DEBUG_CONFIG.LEAGUE_NAME
            });
        } catch (error) {
            console.error('Error loading debug data:', error);
            // No fallbacks - if API fails, the system should fail loudly
            throw error;
            setRealData({
                user: DEBUG_CONFIG.USER,
                leagueId: DEBUG_CONFIG.LEAGUE_ID,
                leagueName: DEBUG_CONFIG.LEAGUE_NAME,
                teamData: { players: [], name: 'Debug Team', score: 0 },
                weekStatus: { current_week: 8 }
            });
        }
        setDataLoading(false);
    };

    // Page definitions with their component dependencies
    const pageDefinitions = {
        'TeamPage': {
            component: TeamPage,
            title: 'Team Page',
            description: 'Main team roster view with player cards',
            components: ['NavigationHeader', 'TeamHeader', 'ExpandablePlayerCard'],
            props: () => ({
                teamId: DEBUG_CONFIG.TEAM_ID,
                leagueId: realData.leagueId,
                leagueName: realData.leagueName,
                setPage: (destination) => {
                    console.log('Debug TeamPage navigation:', destination);
                    switch(destination) {
                        case 'HOME':
                            console.log('HOME navigation - navigating to league selection (simulated MyTeams)');
                            alert('🏠 HOME Navigation\n\nIn production, this would take you to:\n→ My Teams / League Selection page\n\nThis simulates the same behavior as Real Login system.');
                            break;
                        case 'LEAGUE':
                            console.log('LEAGUE navigation - navigating to standings');
                            alert('🏆 LEAGUE Navigation\n\nIn production, this would take you to:\n→ League Standings page\n\nThis simulates the same behavior as Real Login system.');
                            break;
                        case 'GEAR':
                            console.log('GEAR navigation - navigating to gear page');
                            alert('🎒 GEAR Navigation\n\nIn production, this would take you to:\n→ Gear Bag page\n\nThis simulates the same behavior as Real Login system.');
                            break;
                        default:
                            console.log('Navigate to page:', destination);
                            break;
                    }
                },
                setSelectedPlayer: (player) => console.log('Selected player:', player),
                viewMode: 'own'
            })
        },
        'GearPage': {
            component: GearPage,
            title: 'Gear Bag Page', 
            description: 'Team gear management and assignment',
            components: ['GearPanel'],
            props: () => ({
                user: realData.user,
                setPage: (page) => console.log('Navigate to page:', page),
                leagueId: realData.leagueId,
                leagueName: realData.leagueName
            })
        },
        'StandingsPage': {
            component: DebugStandingsPage,
            title: 'League Standings',
            description: 'League rankings and team comparison',
            components: ['TeamRankingRow'],
            props: () => ({
                user: realData.user,
                setPage: (page) => console.log('Navigate to page:', page),
                leagueId: realData.leagueId,
                leagueName: realData.leagueName,
                setSelectedOpponentTeam: (team) => console.log('Selected opponent:', team)
            })
        },
        'DraftPage': {
            component: DraftPage,
            title: 'Sequential Pack Draft System',
            description: 'New Phase 4 draft system with sequential pack opening',
            components: ['SequentialPackSystem'],
            props: () => ({
                user: realData.user,
                setPage: (page) => console.log('Navigate to page:', page),
                leagueId: realData.leagueId,
                leagueName: realData.leagueName
            })
        },
        'RealLoginPage': {
            component: AuthenticationScreen,
            title: 'Real Login System - Auth Screen',
            description: 'Login splash (Sign In popup + Sign Up to the beta key page)',
            components: ['AuthenticationScreen', 'LoginSplashScreen'],
            props: () => ({
                onLogin: (user) => console.log('User logged in:', user),
                // Both navigation handlers are REQUIRED — AuthenticationScreen throws
                // on click rather than rendering a dead button, so a preview that
                // omitted them would blow up when the tester pressed Sign Up.
                onBetaKeyFlow: () => console.log('Beta key flow started'),
                onRequestBetaKey: () => console.log('Beta key request started')
            })
        },
        'MyTeamsPage': {
            component: MyTeams,
            title: 'Real Login System - My Teams',
            description: 'Team management page with actual team data from Real Login system',
            components: ['MyTeams'],
            props: () => ({
                user: { id: 1, username: 'testuser', email: 'test@example.com' },
                onNewTeam: () => console.log('New team clicked'),
                onBack: () => console.log('Back clicked'),
                onTeamClick: (teamId) => console.log('Team clicked:', teamId)
            })
        },
        'LeagueSignUpPage': {
            component: LeagueSignUp,
            title: 'Real Login System - League Sign Up',
            description: 'League creation and joining interface',
            components: ['LeagueSignUp'],
            props: () => ({
                user: { id: 1, username: 'testuser' },
                onDraftTeam: () => console.log('Draft team'),
                onCreatePrivate: () => console.log('Create private'),
                onJoinPrivate: () => console.log('Join private'),
                onMyTeams: () => console.log('My teams'),
                onBack: () => console.log('Back')
            })
        },
    };

    // Component definitions with sample data using real API structure
    const componentDefinitions = {
        'NavigationHeader': {
            component: NavigationHeader,
            title: 'Navigation Header',
            description: 'Top navigation bar with league/team/gear buttons',
            usedInPages: ['TeamPage'],
            props: () => ({
                onNavigate: (dest) => console.log('Navigate to:', dest),
                isOpponentView: false
            })
        },
        'TeamHeader': {
            component: TeamHeader,
            title: 'Team Header',
            description: 'Team name, score, and week display',
            usedInPages: ['TeamPage'],
            props: () => ({
                teamName: realData.teamData?.display_name || 'Debug Team',
                totalScore: realData.teamData?.score || 123.45,
                currentWeek: realData.weekStatus?.current_week || 8,
                isOpponentView: false
            })
        },
        'ExpandablePlayerCard': {
            component: ExpandablePlayerCard,
            title: 'Expandable Player Card',
            description: 'Player card with expandable score breakdown and gear',
            usedInPages: ['TeamPage'],
            props: () => ({
                player: realData.teamData?.players?.[0] || {
                    id: 1,
                    name: 'Josh Allen',
                    position: 'QB',
                    score: 76.7,
                    statString: '24 × 300PASS YRDS 76.7PT',
                    active: true
                },
                isExpanded: false,
                viewMode: 'basic',
                onExpand: () => console.log('Expand player'),
                onScoreClick: () => console.log('Score clicked'),
                onGearClick: () => console.log('Gear clicked'),
                teamId: realData.user?.id || 1,
                leagueId: realData.leagueId || 1,
                isOpponentView: false,
                canModifyGear: true,
                allTeamPlayers: realData.teamData?.players || []
            })
        },
        'GearPanel': {
            component: GearPanel,
            title: 'Gear Panel',
            description: 'Individual gear piece with assignment controls',
            usedInPages: ['GearPage'],
            props: () => ({
                gear: {
                    id: 1,
                    name: 'CAMPBELLS CHUNKY SOUP',
                    slot: 'HANDS',
                    description: 'ADD 2PTS FOR EVERY BROKEN TACKLE',
                    character_sheet: null
                },
                teamPlayers: realData.teamData?.players || [],
                onAssign: (gearId, playerId) => console.log('Assign gear:', gearId, 'to player:', playerId),
                onUnassign: (gearId) => console.log('Unassign gear:', gearId),
                isAssigning: false
            })
        },
        'Standings': {
            component: Standings,
            title: 'League Standings',
            description: 'League standings table',
            usedInPages: ['StandingsPage'],
            props: () => ({
                leagueData: realData.weekStatus || { name: 'Debug League', current_week: 8 },
                teams: [
                    { id: 1, name: 'Team 1', wins: 6, losses: 1, totalScore: 456.2 },
                    { id: 2, name: 'Team 2', wins: 5, losses: 2, totalScore: 423.1 }
                ]
            })
        },
        'TeamRankingRow': {
            component: TeamRankingRow,
            title: 'Team Ranking Row',
            description: 'Individual team row in standings',
            usedInPages: ['StandingsPage'],
            props: () => ({
                team: { id: 1, name: 'Debug Team', wins: 6, losses: 1, totalScore: 456.2 },
                rank: 1,
                isOwnTeam: true,
                onTeamClick: (teamId) => console.log('Team clicked:', teamId)
            })
        },
        'SequentialPackSystem': {
            component: () => <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                🎯 Sequential Pack System Component
                <br />
                <small>Use DraftPage to test the full system</small>
            </div>,
            title: 'Sequential Pack System',
            description: 'New Phase 4 pack opening system with 5-card selection',
            usedInPages: ['DraftPage'],
            props: () => ({})
        },
        'PlayerPackBanner': {
            component: ({ onClick, available, remaining }) => (
                <div
                    onClick={onClick}
                    style={{
                        cursor: available ? 'pointer' : 'not-allowed',
                        textAlign: 'center',
                        background: available ? 'rgba(255,255,255,0.9)' : 'rgba(200,200,200,0.5)',
                        padding: '30px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                        minWidth: '200px',
                        opacity: available ? 1 : 0.6
                    }}
                >
                    <div style={{
                        width: '120px',
                        height: '160px',
                        backgroundColor: available ? '#3498db' : '#7f8c8d',
                        margin: '0 auto 15px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: 'bold'
                    }}>
                        PLAYER<br/>PACK
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: available ? '#2c3e50' : '#7f8c8d' }}>
                        x{remaining}
                    </div>
                    <div style={{ color: available ? '#666' : '#999', fontSize: '14px' }}>
                        {available ? 'Draft 5 players' : 'No packs available'}
                    </div>
                </div>
            ),
            title: 'Player Pack Banner',
            description: 'Large clickable player pack display (legacy)',
            usedInPages: ['Legacy System'],
            props: () => ({
                onClick: () => console.log('Player pack clicked'),
                available: true,
                remaining: 2
            })
        },
        'GearPackBanner': {
            component: ({ onClick, available, remaining }) => (
                <div
                    onClick={onClick}
                    style={{
                        cursor: available ? 'pointer' : 'not-allowed',
                        textAlign: 'center',
                        background: available ? 'rgba(255,255,255,0.9)' : 'rgba(200,200,200,0.5)',
                        padding: '30px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                        minWidth: '200px',
                        opacity: available ? 1 : 0.6
                    }}
                >
                    <div style={{
                        width: '120px',
                        height: '160px',
                        backgroundColor: available ? '#e74c3c' : '#7f8c8d',
                        margin: '0 auto 15px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: 'bold'
                    }}>
                        GEAR<br/>PACK
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: available ? '#2c3e50' : '#7f8c8d' }}>
                        x{remaining}
                    </div>
                    <div style={{ color: available ? '#666' : '#999', fontSize: '14px' }}>
                        {available ? 'Draft 5 gear' : 'No packs available'}
                    </div>
                </div>
            ),
            title: 'Gear Pack Banner',
            description: 'Large clickable gear pack display (legacy)',
            usedInPages: ['Legacy System'],
            props: () => ({
                onClick: () => console.log('Gear pack clicked'),
                available: true,
                remaining: 1
            })
        },
        'DraftProgressPanel': {
            component: ({ players = 3, totalPlayers = 10, gear = 1, totalGear = 3, qb = 1, rb = 2, wr = 0 }) => (
                <div style={{
                    background: 'rgba(255,255,255,0.95)',
                    padding: '20px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}>
                    <h2 style={{ margin: '0 0 15px 0', color: '#2c3e50' }}>Draft Progress</h2>
                    <div style={{ display: 'flex', gap: '40px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div>
                            <strong style={{ color: '#3498db' }}>Players:</strong> {players}/{totalPlayers}
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                QB: {qb}/2, RB: {rb}/4, WR: {wr}/4
                            </div>
                        </div>
                        <div>
                            <strong style={{ color: '#e74c3c' }}>Gear:</strong> {gear}/{totalGear}
                        </div>
                        {players >= totalPlayers && gear >= totalGear && (
                            <div style={{
                                color: '#27ae60',
                                fontWeight: 'bold',
                                background: 'rgba(39, 174, 96, 0.1)',
                                padding: '8px 12px',
                                borderRadius: '4px'
                            }}>
                                ✓ Draft Complete!
                            </div>
                        )}
                    </div>
                </div>
            ),
            title: 'Draft Progress Panel',
            description: 'Shows current draft progress and completion status',
            usedInPages: ['DraftPage'],
            props: () => ({
                players: 3,
                totalPlayers: 10,
                gear: 1,
                totalGear: 5,
                qb: 1,
                rb: 2,
                wr: 0
            })
        },
        'PackCardDisplay': {
            component: ({ cards = [], onSelect, packType = 'PLAYER' }) => (
                <div style={{
                    display: 'flex',
                    gap: '15px',
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                }}>
                    {[
                        { name: 'Josh Allen', position: 'QB', opponent: 'vs BUF' },
                        { name: 'Derrick Henry', position: 'RB', opponent: 'vs TEN' },
                        { name: 'Davante Adams', position: 'WR', opponent: 'vs LV' },
                        { name: 'Travis Kelce', position: 'TE', opponent: 'vs KC' },
                        { name: 'Justin Tucker', position: 'K', opponent: 'vs BAL' }
                    ].map((card, index) => (
                        <div
                            key={index}
                            onClick={() => onSelect && onSelect(card)}
                            style={{
                                width: '120px',
                                height: '160px',
                                position: 'relative',
                                cursor: 'pointer',
                                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                                transform: 'scale(1)',
                                transition: 'all 0.3s ease',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                backgroundImage: 'url(/art/PlayerCard.png)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                            }}
                        >
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(0,0,0,0.1)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                padding: '10px',
                                color: 'white'
                            }}>
                                <div style={{
                                    background: 'rgba(0,0,0,0.8)',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    textAlign: 'center'
                                }}>
                                    {card.name}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    <div style={{
                                        background: '#3498db',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        textAlign: 'center'
                                    }}>
                                        {card.position}
                                    </div>
                                    <div style={{
                                        background: 'rgba(0,0,0,0.8)',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '10px',
                                        textAlign: 'center'
                                    }}>
                                        {card.opponent}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ),
            title: 'Pack Card Display',
            description: '5-card grid for pack opening selection',
            usedInPages: ['DraftPage'],
            props: () => ({
                cards: [],
                onSelect: (card) => console.log('Card selected:', card),
                packType: 'PLAYER'
            })
        },
        'TeamStatusPanel': {
            component: ({ players = [], gear = [] }) => (
                <div style={{
                    width: '250px',
                    background: 'rgba(255,255,255,0.95)',
                    padding: '20px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                    maxHeight: '400px',
                    overflowY: 'auto'
                }}>
                    <h4 style={{ margin: '0 0 15px 0', color: '#2c3e50' }}>Current Draft</h4>
                    
                    <div style={{ marginBottom: '20px' }}>
                        <h5 style={{ margin: '0 0 10px 0', color: '#3498db' }}>
                            Players ({players.length}/10)
                        </h5>
                        {players.length === 0 ? (
                            <div style={{ fontSize: '12px', color: '#999' }}>No players drafted yet</div>
                        ) : (
                            players.map((player, i) => (
                                <div key={i} style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                                    • {player.name} ({player.position})
                                </div>
                            ))
                        )}
                    </div>

                    <div>
                        <h5 style={{ margin: '0 0 10px 0', color: '#e74c3c' }}>
                            Gear ({gear.length}/3)
                        </h5>
                        {gear.length === 0 ? (
                            <div style={{ fontSize: '12px', color: '#999' }}>No gear drafted yet</div>
                        ) : (
                            gear.map((gearPiece, i) => (
                                <div key={i} style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                                    • {gearPiece.name}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            ),
            title: 'Team Status Panel',
            description: 'Fixed panel showing current draft status',
            usedInPages: ['DraftPage'],
            props: () => ({
                players: [
                    { name: 'Josh Allen', position: 'QB' },
                    { name: 'Derrick Henry', position: 'RB' },
                    { name: 'Davante Adams', position: 'WR' }
                ],
                gear: [
                    { name: 'CAMPBELLS CHUNKY SOUP' }
                ]
            })
        },
        'AuthenticationScreen': {
            component: AuthenticationScreen,
            title: 'Authentication Screen',
            description: 'Real Login system main auth component',
            usedInPages: ['RealLoginPage'],
            props: () => ({
                onLogin: (user) => console.log('User logged in:', user),
                onBetaKeyFlow: () => console.log('Beta key flow started'),
                onRequestBetaKey: () => console.log('Beta key request started')
            })
        },
        'MyTeams': {
            component: MyTeams,
            title: 'My Teams Component',
            description: 'Team list and management from Real Login system',
            usedInPages: ['MyTeamsPage'],
            props: () => ({
                user: { id: 1, username: 'testuser', email: 'test@example.com' },
                onNewTeam: () => console.log('New team clicked'),
                onBack: () => console.log('Back clicked'),
                onTeamClick: (teamId) => console.log('Team clicked:', teamId)
            })
        },
        'LeagueSignUp': {
            component: LeagueSignUp,
            title: 'League Sign Up Component',
            description: 'League creation interface from Real Login system',
            usedInPages: ['LeagueSignUpPage'],
            props: () => ({
                user: { id: 1, username: 'testuser' },
                onDraftTeam: () => console.log('Draft team'),
                onCreatePrivate: () => console.log('Create private'),
                onJoinPrivate: () => console.log('Join private'),
                onMyTeams: () => console.log('My teams'),
                onBack: () => console.log('Back')
            })
        },
        'BetaKeyRequest': {
            component: BetaKeyRequest,
            title: 'Beta Key Request',
            description: 'Beta key request form component',
            usedInPages: ['RealLoginPage'],
            props: () => ({
                onBack: () => console.log('Back to auth')
            })
        },
        'BetaKeyValidation': {
            component: BetaKeyValidation,
            title: 'Beta Key Validation',
            description: 'Beta key validation form component',
            usedInPages: ['RealLoginPage'],
            props: () => ({
                onValidated: (key) => console.log('Key validated:', key),
                onBack: () => console.log('Back to auth')
            })
        },
        'UserRegistration': {
            component: UserRegistration,
            title: 'User Registration',
            description: 'User registration form component',
            usedInPages: ['RealLoginPage'],
            props: () => ({
                betaKey: 'TEST-BETA-KEY',
                onRegistered: (user) => console.log('User registered:', user),
                onBack: () => console.log('Back to auth')
            })
        }
    };

    const handleComponentClick = (componentName) => {
        setDebugMode('components');
        setSelectedItem(componentName);
    };

    if (dataLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f8f9fa'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <h2 style={{ color: '#2c3e50' }}>🔄 Loading Debug Data...</h2>
                    <p style={{ color: '#666' }}>Fetching real data from League {DEBUG_CONFIG.LEAGUE_ID}</p>
                </div>
            </div>
        );
    }

    // Show selected page or component in full screen
    if (selectedItem) {
        const isPage = pageDefinitions[selectedItem];
        const isComponent = componentDefinitions[selectedItem];
        const definition = isPage || isComponent;
        const Component = definition.component;
        const props = definition.props();

        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
                {/* Simple header with back button */}
                <div style={{
                    backgroundColor: '#2c3e50',
                    color: 'white',
                    padding: '10px 20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h3 style={{ margin: 0 }}>
                        {isPage ? '📄' : '🧩'} {definition.title}
                    </h3>
                    <button
                        onClick={() => setSelectedItem(null)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        ← Back
                    </button>
                </div>
                
                <Component {...props} />
            </div>
        );
    }

    // Main debug interface
    return (
        <div style={{ padding: '20px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            {/* Header with mode toggles */}
            <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h2 style={{ margin: 0, color: '#2c3e50' }}>🐛 Debug System</h2>
                    <div>
                        {setPage && (
                            <button
                                onClick={() => setPage()}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginRight: '10px'
                                }}
                            >
                                ← Back to Login
                            </button>
                        )}
                        <button
                            onClick={loadRealData}
                            disabled={dataLoading}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: dataLoading ? '#6c757d' : '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: dataLoading ? 'not-allowed' : 'pointer'
                            }}
                        >
                            🔄 Refresh Data
                        </button>
                    </div>
                </div>
                
                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                    <button
                        onClick={() => setDebugMode('pages')}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: debugMode === 'pages' ? '#007bff' : '#e9ecef',
                            color: debugMode === 'pages' ? 'white' : '#333',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}
                    >
                        📄 Pages ({Object.keys(pageDefinitions).length})
                    </button>
                    <button
                        onClick={() => setDebugMode('components')}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: debugMode === 'components' ? '#6f42c1' : '#e9ecef',
                            color: debugMode === 'components' ? 'white' : '#333',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}
                    >
                        🧩 Components ({Object.keys(componentDefinitions).length})
                    </button>
                </div>
                
                <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                    Testing with <strong>{realData.leagueName || 'Loading...'}</strong> - <strong>{realData.user?.name || 'Loading...'}</strong>
                    {realData.user?.id && <span> (Team ID: {realData.user.id})</span>}
                </p>
            </div>

            {/* Content area */}
            {debugMode === 'pages' ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
                    {Object.entries(pageDefinitions).map(([pageName, page]) => (
                        <div key={pageName} style={{
                            backgroundColor: 'white',
                            border: '2px solid #e9ecef',
                            borderRadius: '8px',
                            padding: '20px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                            <h3 style={{ 
                                margin: '0 0 10px 0', 
                                color: '#007bff',
                                cursor: 'pointer'
                            }}
                            onClick={() => setSelectedItem(pageName)}
                            >
                                📄 {page.title}
                            </h3>
                            <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#666' }}>
                                {page.description}
                            </p>
                            
                            {page.components.length > 0 && (
                                <div>
                                    <strong style={{ fontSize: '13px', color: '#495057' }}>Components:</strong>
                                    <div style={{ marginTop: '8px' }}>
                                        {page.components.map(componentName => (
                                            <button
                                                key={componentName}
                                                onClick={() => handleComponentClick(componentName)}
                                                style={{
                                                    padding: '4px 8px',
                                                    margin: '2px',
                                                    fontSize: '11px',
                                                    backgroundColor: '#6f42c1',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '3px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                🧩 {componentName}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            <button
                                onClick={() => setSelectedItem(pageName)}
                                style={{
                                    marginTop: '15px',
                                    padding: '8px 16px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    width: '100%'
                                }}
                            >
                                Test Page
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                    {Object.entries(componentDefinitions).map(([componentName, component]) => (
                        <div
                            key={componentName}
                            onClick={() => setSelectedItem(componentName)}
                            style={{
                                padding: '15px',
                                backgroundColor: 'white',
                                border: '2px solid #e9ecef',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            <div style={{ fontWeight: 'bold', color: '#6f42c1', marginBottom: '5px' }}>
                                🧩 {component.title}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                                {component.description}
                            </div>
                            {component.usedInPages.length > 0 && (
                                <div style={{ fontSize: '11px', color: '#28a745' }}>
                                    📄 Used in: {component.usedInPages.join(', ')}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default DebugSystem;